import React, { useEffect, useState } from 'react';
import { BellIcon, DollarSignIcon, UserPlusIcon, XIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface Notification {
  id: string;
  type: 'commission' | 'signup';
  message: string;
  amount?: number;
  timestamp: Date;
  read: boolean;
}
export const AffiliateNotifications: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  // Simulate fetching notifications
  useEffect(() => {
    // Mock data for demonstration
    const mockNotifications: Notification[] = [{
      id: '1',
      type: 'commission',
      message: 'You earned a commission from Product Template Pro',
      amount: 12.5,
      timestamp: new Date(Date.now() - 1000 * 60 * 30),
      read: false
    }, {
      id: '2',
      type: 'signup',
      message: 'New user signed up using your referral link',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2),
      read: false
    }, {
      id: '3',
      type: 'commission',
      message: 'You earned a commission from UI Kit Bundle',
      amount: 25.75,
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24),
      read: true
    }, {
      id: '4',
      type: 'signup',
      message: 'New user signed up using your referral link',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 48),
      read: true
    }];
    setNotifications(mockNotifications);
    setUnreadCount(mockNotifications.filter(n => !n.read).length);
  }, []);
  const markAllAsRead = () => {
    setNotifications(notifications.map(notification => ({
      ...notification,
      read: true
    })));
    setUnreadCount(0);
  };
  const markAsRead = (id: string) => {
    setNotifications(notifications.map(notification => notification.id === id ? {
      ...notification,
      read: true
    } : notification));
    setUnreadCount(prev => Math.max(prev - 1, 0));
  };
  const deleteNotification = (id: string) => {
    const notification = notifications.find(n => n.id === id);
    setNotifications(notifications.filter(notification => notification.id !== id));
    if (notification && !notification.read) {
      setUnreadCount(prev => Math.max(prev - 1, 0));
    }
  };
  const formatTimestamp = (date: Date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      return `${Math.floor(diffInSeconds / 60)}m ago`;
    } else if (diffInSeconds < 86400) {
      return `${Math.floor(diffInSeconds / 3600)}h ago`;
    } else {
      return `${Math.floor(diffInSeconds / 86400)}d ago`;
    }
  };
  return <div className="relative" data-id="element-118">
      <button onClick={() => setShowNotifications(!showNotifications)} className="relative p-2 text-gray-600 dark:text-[#B0C1BD] hover:text-[#2DB489] dark:hover:text-[#4ED1A0] transition-colors" data-id="element-119">
        <BellIcon className="h-6 w-6" data-id="element-120" />
        {unreadCount > 0 && <span className="absolute top-0 right-0 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-[#2DB489] dark:bg-[#4ED1A0] rounded-full" data-id="element-121">
            {unreadCount}
          </span>}
      </button>
      <AnimatePresence data-id="element-122">
        {showNotifications && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 10
      }} className="absolute right-0 mt-2 w-80 bg-white dark:bg-[#162825] rounded-lg shadow-lg z-50 overflow-hidden" data-id="element-123">
            <div className="p-4 border-b border-gray-100 dark:border-[#1A3330] flex justify-between items-center" data-id="element-124">
              <h3 className="font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-125">
                Notifications
              </h3>
              {unreadCount > 0 && <button onClick={markAllAsRead} className="text-sm text-[#2DB489] dark:text-[#4ED1A0] hover:underline" data-id="element-126">
                  Mark all as read
                </button>}
            </div>
            <div className="max-h-96 overflow-y-auto" data-id="element-127">
              {notifications.length === 0 ? <div className="p-4 text-center text-gray-500 dark:text-[#7A8F8A]" data-id="element-128">
                  No notifications yet
                </div> : notifications.map(notification => <div key={notification.id} className={`p-4 border-b border-gray-100 dark:border-[#1A3330] relative ${!notification.read ? 'bg-blue-50 dark:bg-[#1A3330]' : ''}`} data-id="element-129">
                    <div className="flex" data-id="element-130">
                      <div className={`mr-3 mt-1 p-2 rounded-full ${notification.type === 'commission' ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' : 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'}`} data-id="element-131">
                        {notification.type === 'commission' ? <DollarSignIcon className="h-4 w-4" data-id="element-132" /> : <UserPlusIcon className="h-4 w-4" data-id="element-133" />}
                      </div>
                      <div className="flex-1" data-id="element-134">
                        <p className="text-sm text-gray-800 dark:text-[#F8FAF9] mb-1" data-id="element-135">
                          {notification.message}
                        </p>
                        {notification.amount && <p className="text-sm font-medium text-green-600 dark:text-green-400" data-id="element-136">
                            ${notification.amount.toFixed(2)}
                          </p>}
                        <p className="text-xs text-gray-500 dark:text-[#7A8F8A] mt-1" data-id="element-137">
                          {formatTimestamp(notification.timestamp)}
                        </p>
                      </div>
                      <button onClick={() => deleteNotification(notification.id)} className="text-gray-400 dark:text-[#7A8F8A] hover:text-gray-600 dark:hover:text-[#B0C1BD]" data-id="element-138">
                        <XIcon className="h-4 w-4" data-id="element-139" />
                      </button>
                    </div>
                    {!notification.read && <button onClick={() => markAsRead(notification.id)} className="text-xs text-[#2DB489] dark:text-[#4ED1A0] hover:underline mt-2" data-id="element-140">
                        Mark as read
                      </button>}
                  </div>)}
            </div>
            <div className="p-3 text-center border-t border-gray-100 dark:border-[#1A3330]" data-id="element-141">
              <button onClick={() => setShowNotifications(false)} className="text-sm text-gray-600 dark:text-[#B0C1BD] hover:text-[#2DB489] dark:hover:text-[#4ED1A0]" data-id="element-142">
                Close
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
};